import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Mail,
  MailOpen,
  Trash2,
  RefreshCw,
  Check,
  CheckCheck,
  Inbox,
  Archive,
  Star,
  AlertCircle,
  Bell,
  Megaphone,
  Gift,
  Settings,
  Info,
  ChevronRight,
  MailWarning,
  Clock,
  Search,
  Filter
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";

const Mailbox = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [activeMessage, setActiveMessage] = useState(null);
  const [filter, setFilter] = useState("all"); // all, unread, starred
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/mailbox/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data.messages || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      // Show empty state if no messages
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageIds) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/mailbox/mark-read`, {
        message_ids: messageIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => prev.map(msg => 
        messageIds.includes(msg.message_id) ? { ...msg, is_read: true } : msg
      ));
      setUnreadCount(prev => Math.max(0, prev - messageIds.length));
      toast.success("Marked as read");
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const markAsUnread = async (messageIds) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/mailbox/mark-unread`, {
        message_ids: messageIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => prev.map(msg => 
        messageIds.includes(msg.message_id) ? { ...msg, is_read: false } : msg
      ));
      setUnreadCount(prev => prev + messageIds.length);
      toast.success("Marked as unread");
    } catch (error) {
      toast.error("Failed to mark as unread");
    }
  };

  const deleteMessages = async (messageIds) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/mailbox/delete`, {
        message_ids: messageIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const deletedUnread = messages.filter(m => messageIds.includes(m.message_id) && !m.is_read).length;
      setMessages(prev => prev.filter(msg => !messageIds.includes(msg.message_id)));
      setUnreadCount(prev => Math.max(0, prev - deletedUnread));
      setSelectedMessages([]);
      setActiveMessage(null);
      toast.success(`Deleted ${messageIds.length} message(s)`);
    } catch (error) {
      toast.error("Failed to delete messages");
    }
  };

  const toggleStar = async (messageId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/mailbox/toggle-star`, {
        message_id: messageId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => prev.map(msg => 
        msg.message_id === messageId ? { ...msg, is_starred: !msg.is_starred } : msg
      ));
    } catch (error) {
      toast.error("Failed to update star");
    }
  };

  const toggleSelectMessage = (messageId) => {
    setSelectedMessages(prev => 
      prev.includes(messageId) 
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    );
  };

  const selectAllMessages = () => {
    if (selectedMessages.length === filteredMessages.length) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(filteredMessages.map(m => m.message_id));
    }
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case "announcement":
        return <Megaphone className="w-4 h-4 text-blue-400" />;
      case "alert":
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case "promotion":
        return <Gift className="w-4 h-4 text-purple-400" />;
      case "update":
        return <Bell className="w-4 h-4 text-amber-400" />;
      case "system":
        return <Settings className="w-4 h-4 text-gray-400" />;
      default:
        return <Mail className="w-4 h-4 text-gray-400" />;
    }
  };

  const getMessageTypeColor = (type) => {
    switch (type) {
      case "announcement":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "alert":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      case "promotion":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "update":
        return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "system":
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === "unread") return !msg.is_read;
    if (filter === "starred") return msg.is_starred;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center">
            <Inbox className="w-5 h-5 text-black" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Inbox</h2>
            <p className="text-sm text-gray-400">
              {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchMessages}
          className="border-gray-700 text-gray-300"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between bg-stone-900/50 rounded-lg p-3 border border-stone-800">
        <div className="flex items-center gap-3">
          <Checkbox
            checked={selectedMessages.length === filteredMessages.length && filteredMessages.length > 0}
            onCheckedChange={selectAllMessages}
            className="border-gray-600"
          />
          
          {selectedMessages.length > 0 ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">{selectedMessages.length} selected</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAsRead(selectedMessages)}
                className="text-gray-400 hover:text-white"
              >
                <MailOpen className="w-4 h-4 mr-1" />
                Read
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAsUnread(selectedMessages)}
                className="text-gray-400 hover:text-white"
              >
                <Mail className="w-4 h-4 mr-1" />
                Unread
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deleteMessages(selectedMessages)}
                className="text-red-400 hover:text-red-300"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {["all", "unread", "starred"].map((f) => (
                <Button
                  key={f}
                  variant={filter === f ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setFilter(f)}
                  className={filter === f ? "bg-amber-500/20 text-amber-400" : "text-gray-400"}
                >
                  {f === "all" && <Inbox className="w-4 h-4 mr-1" />}
                  {f === "unread" && <Mail className="w-4 h-4 mr-1" />}
                  {f === "starred" && <Star className="w-4 h-4 mr-1" />}
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                  {f === "unread" && unreadCount > 0 && (
                    <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5">{unreadCount}</Badge>
                  )}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Messages List */}
      <div className="grid lg:grid-cols-5 gap-4">
        {/* Message List */}
        <div className="lg:col-span-2">
          <Card className="bg-stone-900 border-stone-800">
            <ScrollArea className="h-[500px]">
              {filteredMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center p-6">
                  <div className="w-16 h-16 bg-stone-800 rounded-full flex items-center justify-center mb-4">
                    <Inbox className="w-8 h-8 text-gray-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-400 mb-2">
                    {filter === "all" ? "No messages yet" : `No ${filter} messages`}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {filter === "all" 
                      ? "Messages from MyHairChair will appear here"
                      : `You don't have any ${filter} messages`
                    }
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-stone-800">
                  {filteredMessages.map((message) => (
                    <div
                      key={message.message_id}
                      className={`p-4 cursor-pointer transition-colors hover:bg-stone-800/50 ${
                        activeMessage?.message_id === message.message_id ? 'bg-stone-800' : ''
                      } ${!message.is_read ? 'bg-amber-500/5' : ''}`}
                      onClick={() => {
                        setActiveMessage(message);
                        if (!message.is_read) {
                          markAsRead([message.message_id]);
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedMessages.includes(message.message_id)}
                          onCheckedChange={(e) => {
                            e.stopPropagation();
                            toggleSelectMessage(message.message_id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="border-gray-600 mt-1"
                        />
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStar(message.message_id);
                          }}
                          className="mt-1"
                        >
                          <Star 
                            className={`w-4 h-4 ${
                              message.is_starred 
                                ? 'text-amber-400 fill-amber-400' 
                                : 'text-gray-600 hover:text-gray-400'
                            }`} 
                          />
                        </button>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {getMessageIcon(message.type)}
                            <span className={`font-semibold text-sm truncate ${!message.is_read ? 'text-white' : 'text-gray-300'}`}>
                              {message.subject}
                            </span>
                            {!message.is_read && (
                              <div className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">
                            {message.preview || message.content?.substring(0, 80)}...
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={`text-xs ${getMessageTypeColor(message.type)}`}>
                              {message.type || 'message'}
                            </Badge>
                            <span className="text-xs text-gray-600">
                              {message.created_at ? formatDistanceToNow(new Date(message.created_at), { addSuffix: true }) : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </Card>
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-3">
          <Card className="bg-stone-900 border-stone-800 h-[500px]">
            {activeMessage ? (
              <div className="h-full flex flex-col">
                {/* Message Header */}
                <div className="p-4 border-b border-stone-800">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        {getMessageIcon(activeMessage.type)}
                        <Badge className={`text-xs ${getMessageTypeColor(activeMessage.type)}`}>
                          {activeMessage.type || 'message'}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-bold text-white">{activeMessage.subject}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        From: <span className="text-amber-400">MyHairChair Team</span>
                        {activeMessage.created_at && (
                          <> • {format(new Date(activeMessage.created_at), 'PPpp')}</>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStar(activeMessage.message_id)}
                      >
                        <Star className={`w-4 h-4 ${activeMessage.is_starred ? 'text-amber-400 fill-amber-400' : 'text-gray-400'}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteMessages([activeMessage.message_id])}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Message Content */}
                <ScrollArea className="flex-1 p-4">
                  <div className="prose prose-invert max-w-none">
                    <div className="whitespace-pre-wrap text-gray-300 leading-relaxed">
                      {activeMessage.content}
                    </div>
                  </div>
                </ScrollArea>
                
                {/* Message Actions */}
                <div className="p-4 border-t border-stone-800 flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => markAsUnread([activeMessage.message_id])}
                    className="border-gray-700 text-gray-300"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Mark Unread
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteMessages([activeMessage.message_id])}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-6">
                <div className="w-20 h-20 bg-stone-800 rounded-full flex items-center justify-center mb-4">
                  <Mail className="w-10 h-10 text-gray-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-400 mb-2">Select a message</h3>
                <p className="text-sm text-gray-500">
                  Click on a message to read it here
                </p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Mailbox;
